/**
 * Utility functions for handling date/timestamp conversions
 */

import { startOfWeek, isSameWeek } from 'date-fns';

/**
 * Converts Firebase timestamp to JavaScript Date object
 * @param {any} timestamp - Firebase timestamp or date string
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const convertTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    // If it's a Firebase Timestamp object
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // If it's already a Date object
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // If it's a string or number
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error converting timestamp:', error);
    return null;
  }
};

/**
 * Formats a timestamp to Vietnamese locale date string
 * @param {any} timestamp - Firebase timestamp or date string
 * @param {string} fallback - Fallback text if timestamp is invalid
 * @returns {string} - Formatted date string
 */
export const formatDate = (timestamp, fallback = 'Không có') => {
  const date = convertTimestamp(timestamp);
  if (!date) return fallback;
  
  try {
    return date.toLocaleDateString('vi-VN');
  } catch (error) {
    console.error('Error formatting date:', error);
    return fallback;
    }
};

/**
 * Formats a timestamp to Vietnamese locale date and time string
 * @param {any} timestamp - Firebase timestamp or date string
 * @param {string} fallback - Fallback text if timestamp is invalid
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (timestamp, fallback = 'Không có') => {
  const date = convertTimestamp(timestamp);
  if (!date) return fallback;
  
  try {
    return date.toLocaleString('vi-VN');
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return fallback;
  }
};

/**
 * Checks if a due date is overdue
 * @param {any} dueDate - Firebase timestamp or date string
 * @param {string} status - Task status
 * @returns {boolean} - True if overdue
 */
export const isOverdue = (dueDate, status = '') => {
  if (!dueDate || status === 'completed') return false;
  
  const date = convertTimestamp(dueDate);
  if (!date) return false;
  
  return date < new Date();
};

/**
 * Gets relative time string (e.g., "2 ngày trước")
 * @param {any} timestamp - Firebase timestamp or date string
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (timestamp) => {
  const date = convertTimestamp(timestamp);
  if (!date) return 'Không có';
  
  try {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Vừa xong';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} phút trước`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} giờ trước`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} ngày trước`;
    
    return formatDate(timestamp);
  } catch (error) {
    console.error('Error getting relative time:', error);
    return formatDate(timestamp);
  }
};

/**
 * Formats a date for Firestore storage
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date string for Firestore
 */
export const formatDateForFirestore = (date) => {
  if (!date) return null;
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    
    // Format as YYYY-MM-DD for Firestore
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for Firestore:', error);
    return null;
  }
};

/**
 * Checks if registration is eligible for a given date
 * @param {Date|string} date - Date to check
 * @param {Object} regWindow - Registration window object
 * @returns {Object} - { canRegister: boolean, canEdit: boolean, canRequestLeave: boolean, reason: string }
 */
export const checkRegistrationEligibility = (date, regWindow) => {
  if (!date) return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'invalid_date' };
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'invalid_date' };
    
    const today = new Date();
    const targetDate = new Date(dateObj);
    
    // Kiểm tra xem có phải ngày trong quá khứ không (chỉ so sánh ngày, không so sánh giờ)
    const todayDate = new Date(today);
    todayDate.setHours(0, 0, 0, 0);
    const targetDateOnly = new Date(targetDate);
    targetDateOnly.setHours(0, 0, 0, 0);
    
    if (targetDateOnly < todayDate) {
      return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'past_date' };
    }
    
    // Kiểm tra xem cổng đăng ký có mở không
    if (!regWindow?.isOpen) {
      return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'registration_closed' };
    }
    
    // Kiểm tra xem có phải ngày trong tuần hiện tại không
    const targetWeekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
    const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    
    // Nếu cổng đăng ký mở, cho phép đăng ký bất kể thứ mấy trong tuần
    // (giới hạn thứ 3 đã được gỡ bỏ - admin có thể mở cổng đăng ký bất cứ lúc nào)
    
    // Nếu là tuần trước, không cho phép đăng ký
    if (targetWeekStart < todayWeekStart) {
      return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'past_week' };
    }
    
    // Tất cả điều kiện đều thỏa mãn
    return { canRegister: true, canEdit: true, canRequestLeave: true, reason: 'allowed' };
  } catch (error) {
    console.error('Error checking registration eligibility:', error);
    return { canRegister: false, canEdit: false, canRequestLeave: false, reason: 'error' };
  }
};

/**
 * Gets calendar days for a month
 * @param {Date|string} date - Date in the month
 * @returns {Array} - Array of calendar days
 */
export const getMonthCalendarDays = (date) => {
  if (!date) return [];
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return [];
    
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Đảm bảo tuần bắt đầu từ thứ 2 (1) và kết thúc chủ nhật (0)
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Nếu là chủ nhật (0) thì trừ 6, còn lại trừ (dayOfWeek - 1)
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  } catch (error) {
    console.error('Error getting month calendar days:', error);
    return [];
  }
};

/**
 * Gets week days for a given date
 * @param {Date|string} date - Date in the week
 * @returns {Array} - Array of week days
 */
export const getWeekDays = (date) => {
  if (!date) return [];
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return [];
    
    const days = [];
    const startOfWeek = new Date(dateObj);
    // Đảm bảo tuần bắt đầu từ thứ 2 (1) và kết thúc chủ nhật (0)
    const dayOfWeek = startOfWeek.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Nếu là chủ nhật (0) thì trừ 6, còn lại trừ (dayOfWeek - 1)
    startOfWeek.setDate(startOfWeek.getDate() - daysToSubtract);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  } catch (error) {
    console.error('Error getting week days:', error);
    return [];
  }
};

/**
 * Gets duration in minutes between two times
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {number} - Duration in minutes
 */
export const getDurationInMinutes = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  try {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    return endMinutes - startMinutes;
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 0;
  }
};